/**
 * reducers/source-types.ts
 * desc: client source types
 * note: this entity describes the type of source from reducers/source.js
 */

import type { Reducer } from '@reduxjs/toolkit';
import type { SourceTypesAction } from '../actions/source-types';
import type { SourceTypeDto } from '../../types/api/source-types';
import type { ComponentInfo } from '../../types/api/components';

export interface SourceTypesState {
    isLoading: {
        createSourceType: boolean;
        deleteSourceType: boolean;
        getSourceType: boolean;
        getSourceTypeUsage: boolean;
        getSourceTypes: boolean;
        updateSourceType: boolean;
    };
    // an empty object until a source type is fetched or updated
    detail: SourceTypeDto | Record<string, never>;
    list: SourceTypeDto[];
    usage: ComponentInfo[];
}

const INITIAL_STATE: SourceTypesState = {
    isLoading: {
        createSourceType: false,
        deleteSourceType: false,
        getSourceType: false,
        getSourceTypeUsage: false,
        getSourceTypes: false,
        updateSourceType: false,
    },
    detail: {},
    list: [],
    usage: []
};

function sourceTypesReducer(state: SourceTypesState = INITIAL_STATE, action: SourceTypesAction): SourceTypesState {
    switch (action.type) {

        // Clear Source Types
        case 'CLEAR_SOURCE_TYPES':
            return _clearSourceTypes();

        // CREATE Source Type (Instance)
        case 'CREATE_SOURCE_TYPE_ERROR':
            return _createSourceTypeError(state);
        case 'CREATE_SOURCE_TYPE_REQUEST':
            return _createSourceTypeRequest(state);
        case 'CREATE_SOURCE_TYPE_SUCCESS':
            return _createSourceTypeSuccess(state, action.payload);

        // DELETE Source Type (Instance)
        case 'DELETE_SOURCE_TYPE_ERROR':
            return _deleteSourceTypeError(state);
        case 'DELETE_SOURCE_TYPE_REQUEST':
            return _deleteSourceTypeRequest(state);
        case 'DELETE_SOURCE_TYPE_SUCCESS':
            return _deleteSourceTypeSuccess(state, action.payload);

        // GET Source Type (Instance)
        case 'GET_SOURCE_TYPE_ERROR':
            return _getSourceTypeError(state);
        case 'GET_SOURCE_TYPE_REQUEST':
            return _getSourceTypeRequest(state);
        case 'GET_SOURCE_TYPE_SUCCESS':
            return _getSourceTypeSuccess(state, action.payload);

        // GET Source Type Usage (Instance)
        case 'GET_SOURCE_TYPE_USAGE_ERROR':
            return _getSourceTypeUsageError(state);
        case 'GET_SOURCE_TYPE_USAGE_REQUEST':
            return _getSourceTypeUsageRequest(state);
        case 'GET_SOURCE_TYPE_USAGE_SUCCESS':
            return _getSourceTypeUsageSuccess(state, action.payload);

        // GET Source Types (Collection)
        case 'GET_SOURCE_TYPES_ERROR':
            return _getSourceTypesError(state);
        case 'GET_SOURCE_TYPES_REQUEST':
            return _getSourceTypesRequest(state);
        case 'GET_SOURCE_TYPES_SUCCESS':
            return _getSourceTypesSuccess(state, action.payload);

        // Update Source Type (Instance)
        case 'UPDATE_SOURCE_TYPE_ERROR':
            return _updateSourceTypeError(state);
        case 'UPDATE_SOURCE_TYPE_REQUEST':
            return _updateSourceTypeRequest(state);
        case 'UPDATE_SOURCE_TYPE_SUCCESS':
            return _updateSourceTypeSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   Clear SourceTypes   *********************
//*************************************************
function _clearSourceTypes(): SourceTypesState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CREATE Source Type (Instance)   *********
//*************************************************
function _createSourceTypeError(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createSourceType: false,
        },
    };
}

function _createSourceTypeRequest(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createSourceType: true,
        },
    };
}

function _createSourceTypeSuccess(state: SourceTypesState, payload: SourceTypeDto): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createSourceType: false,
        },
        list: [ ...state.list, payload ],
    };
}



//*************************************************
//*****   DELETE Source Type (Instance)   *********
//*************************************************
function _deleteSourceTypeError(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteSourceType: false,
        },
    };
}

function _deleteSourceTypeRequest(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteSourceType: true,
        },
    };
}

function _deleteSourceTypeSuccess(state: SourceTypesState, sourceTypeId: string): SourceTypesState {
    // create new list without deleted source type
    const [ ...sourceTypesList ] = state.list;
    const deletedIndex = state.list.findIndex(x => x.id === sourceTypeId);
    if (deletedIndex > -1) sourceTypesList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteSourceType: false,
        },
        list: sourceTypesList,
    };
}



//*************************************************
//*****   GET Source Type (Instance)   ************
//*************************************************
function _getSourceTypeError(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceType: false,
        },
    };
}

function _getSourceTypeRequest(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceType: true,
        },
    };
}

function _getSourceTypeSuccess(state: SourceTypesState, payload: SourceTypeDto): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceType: false,
        },
        detail: payload
    };
}



//*************************************************
//*****   GET Source Type Usage (Instance)   ******
//*************************************************
function _getSourceTypeUsageError(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceTypeUsage: false,
        },
    };
}

function _getSourceTypeUsageRequest(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceTypeUsage: true,
        },
    };
}

function _getSourceTypeUsageSuccess(state: SourceTypesState, payload: ComponentInfo[]): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceTypeUsage: false,
        },
        usage: payload
    };
}



//*************************************************
//*****   GET Source Types (Collection)   *********
//*************************************************
function _getSourceTypesError(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceTypes: false,
        },
    };
}

function _getSourceTypesRequest(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getSourceTypes: true,
        },
    };
}

function _getSourceTypesSuccess(state: SourceTypesState, payload: SourceTypeDto[]): SourceTypesState {
    return {
        ...state,
        list: payload,
        isLoading: {
            ...state.isLoading,
            getSourceTypes: false,
        }
    };
}

export default sourceTypesReducer as Reducer<SourceTypesState>;



//*************************************************
//*****   UPDATE Source Type (Instance)   *********
//*************************************************
function _updateSourceTypeError(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateSourceType: false,
        },
    };
}

function _updateSourceTypeRequest(state: SourceTypesState): SourceTypesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateSourceType: true,
        },
    };
}

function _updateSourceTypeSuccess(state: SourceTypesState, payload: SourceTypeDto): SourceTypesState {
    // create new list w updated source type
    const [ ...sourceTypesList ] = state.list;
    const updatedIndex = state.list.findIndex(x => x.id === payload.id);
    if (updatedIndex > -1) {
        sourceTypesList[updatedIndex] = payload;
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateSourceType: false,
        },
        list: sourceTypesList,
        detail: payload
    };
}
